import { Edit2Icon, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import axios from "axios";
import EditModal from "../../components/modal/EditModal";
import DeleteModal from "../../components/modal/DeleteModal";
import useUserRole from "../../app/auth/useUserRole";
import { getToken } from "../../app/auth/authConfig";

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const currentUserRole = useUserRole();
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/categories/`;
      const token = await getToken();

      const res = await axios.get(baseURL, {
        headers: { "oidc-token": token },
      });
      setCategories(res.data || []);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCategories = categories.filter(
    (c) =>
      c.categoryName.toLowerCase().includes(search.toLowerCase()) ||
      c.description.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCategories.length / perPage) || 1;
  const paginatedCategories = filteredCategories.slice(
    (page - 1) * perPage,
    page * perPage
  );
  const skeletonRowKeys = Array.from({ length: perPage }, (_, i) => `skeleton-row-${i + 1}`);

  const handleDelete = async () => {
    if (deleteIndex === null || deleteIndex === undefined) return;

    try {
      setIsDeleting(true);
      const cat = filteredCategories[deleteIndex];
      if (!cat?.id) {
        return;
      }

      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/categories/${cat.id}`;
      const token = await getToken();

      await axios.delete(baseURL, {
        headers: { "oidc-token": token },
      });

      setShowDeleteModal(false);
      setDeleteIndex(null);
      await fetchCategories();
    } catch (err) {
      console.error("Delete error", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCategorySave = async (formData) => {
    try {
      if (!editData?.id) {
        return;
      }

      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/categories/${editData.id}`;
      const token = await getToken();

      await axios.put(
        baseURL,
        {
          categoryName: formData.categoryName,
          description: formData.description,
        },
        {
          headers: { "oidc-token": token },
        }
      );

      setShowEditModal(false);
      setEditData(null);
      await fetchCategories();
    } catch (err) {
      console.error("Update error", err);
    }
  };

  const toggleDescription = (categoryId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  let tableRows;

  if (isLoading) {
    tableRows = skeletonRowKeys.map((rowKey) => (
      <tr
        key={rowKey}
        style={{
          background: "#f8f8f8",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          border: "1px solid #e5e5e5",
        }}
      >
        <td
          style={{
            padding: "14px 16px",
            borderRadius: "8px 0 0 8px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div className="h-4 bg-gray-300 rounded w-40 animate-pulse" />
        </td>
        <td
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div className="h-4 bg-gray-300 rounded w-56 animate-pulse" />
        </td>
        <td
          style={{
            padding: "14px 16px",
            borderRadius: "0 8px 8px 0",
            borderBottom: "1px solid #f0f0f0",
            textAlign: "center",
          }}
        >
          <div className="flex gap-2 justify-center">
            <div className="h-8 bg-gray-300 rounded w-20 animate-pulse" />
            <div className="h-8 bg-gray-300 rounded w-20 animate-pulse" />
          </div>
        </td>
      </tr>
    ));
  } else if (paginatedCategories.length === 0) {
    tableRows = (
      <tr>
        <td
          colSpan="3"
          style={{
            padding: "40px 16px",
            textAlign: "center",
            background: "#f8f8f8",
            borderRadius: "8px",
          }}
        >
          <span style={{ color: "#999", fontSize: "15px" }}>
            📭 No categories found
          </span>
        </td>
      </tr>
    );
  } else {
    tableRows = paginatedCategories.map((cat, idx) => {
      const descriptionText = cat.description || "";
      const isLongDescription = descriptionText.length > 100;
      const isExpanded = Boolean(expandedDescriptions[cat.id]);
      let visibleDescription = descriptionText;

      if (!isExpanded && isLongDescription) {
        visibleDescription = `${descriptionText.slice(0, 100)}...`;
      }

      return (
        <tr
          key={cat.id}
          style={{
            background: idx % 2 === 0 ? "#bb1ccc0d" : "#f3f0f7",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            border: "1px solid #e5e5e5",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <td
            style={{
              padding: "14px 16px",
              borderRadius: "8px 0 0 8px",
              fontWeight: "500",
              color: "#333",
            }}
          >
            {cat.categoryName}
          </td>
          <td
            style={{
              padding: "14px 16px",
              color: "#666",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
            title={descriptionText}
          >
            <span>{visibleDescription}</span>
            {isLongDescription && (
              <button
                type="button"
                onClick={() => toggleDescription(cat.id)}
                style={{
                  marginLeft: 8,
                  background: "none",
                  border: "none",
                  color: "#bb1ccc",
                  fontWeight: "600",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {isExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </td>
          <td
            style={{
              padding: "14px 16px",
              borderRadius: "0 8px 8px 0",
              textAlign: "center",
            }}
          >
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  setShowEditModal(true);
                  setEditData(cat);
                }}
                disabled={currentUserRole !== "Admin" && currentUserEmail.toLowerCase() !== (cat.createdBy || "").toLowerCase()}
                title={currentUserRole !== "Admin" && currentUserEmail.toLowerCase() !== (cat.createdBy || "").toLowerCase() ? "You can only edit categories you created" : "Edit category"}
                style={{
                  background: (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) ? "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)" : "#ccc",
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) ? "pointer" : "not-allowed",
                  fontSize: "12px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.3s ease",
                  opacity: (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) ? 1 : 0.6,
                }}
                onMouseEnter={(e) => {
                  if (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(187,28,204,0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Edit2Icon width={14} /> Edit
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(true);
                  setDeleteIndex((page - 1) * perPage + idx);
                }}
                disabled={currentUserRole !== "Admin" && currentUserEmail.toLowerCase() !== (cat.createdBy || "").toLowerCase()}
                title={currentUserRole !== "Admin" && currentUserEmail.toLowerCase() !== (cat.createdBy || "").toLowerCase() ? "You can only delete categories you created" : "Delete category"}
                style={{
                  background: (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" : "#ccc",
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) ? "pointer" : "not-allowed",
                  fontSize: "12px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.3s ease",
                  opacity: (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) ? 1 : 0.6,
                }}
                onMouseEnter={(e) => {
                  if (currentUserRole === "Admin" || currentUserEmail.toLowerCase() === (cat.createdBy || "").toLowerCase()) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(239,68,68,0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Trash2 width={14} /> Delete
              </button>
            </div>
          </td>
        </tr>
      );
    });
  }

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          padding: 32,
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Search category name or description"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            disabled={isLoading}
            style={{
              padding: 8,
              width: 300,
              borderRadius: 4,
              border: "1px solid #ccc",
              marginBottom: 8,
              backgroundColor: isLoading ? "#f0f0f0" : "white",
              cursor: isLoading ? "not-allowed" : "auto",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span style={{ fontWeight: "bold" }}>
              Total Categories {filteredCategories.length}
            </span>
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || isLoading}
                style={{
                  padding: "4px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page === 1 || isLoading ? "#eee" : "#fff",
                  cursor: page === 1 || isLoading ? "not-allowed" : "pointer",
                }}
              >
                Prev
              </button>
              <span style={{ margin: "0 8px" }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || isLoading}
                style={{
                  padding: "4px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page === totalPages || isLoading ? "#eee" : "#fff",
                  cursor: page === totalPages || isLoading ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: "0 8px",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ height: "50px" }}>
                <th
                  style={{
                    background: "#bb1ccc",
                    color: "white",
                    fontWeight: "600",
                    padding: "14px 16px",
                    textAlign: "left",
                    borderRadius: "8px 0 0 8px",
                    fontSize: "13px",
                    letterSpacing: "0.5px",
                    width: "45%",
                  }}
                >
                  🗂️ Category Name
                </th>
                <th
                  style={{
                    background: "#bb1ccc",
                    color: "white",
                    fontWeight: "600",
                    padding: "14px 16px",
                    textAlign: "left",
                    fontSize: "13px",
                    letterSpacing: "0.5px",
                    width: "35%",
                  }}
                >
                  📝 Description
                </th>
                <th
                  style={{
                    background: "#bb1ccc",
                    color: "white",
                    fontWeight: "600",
                    padding: "14px 16px",
                    textAlign: "center",
                    borderRadius: "0 8px 8px 0",
                    fontSize: "13px",
                    letterSpacing: "0.5px",
                    width: "20%",
                  }}
                >
                  ⚙️ Actions
                </th>
              </tr>
            </thead>
            <tbody>{tableRows}</tbody>
          </table>
        </div>
      </div>

      {showEditModal && editData && (
        <EditModal
          title="Edit Category"
          fields={[
            { label: "Category Name", key: "categoryName" },
            { label: "Description", key: "description" },
          ]}
          data={editData}
          onClose={() => setShowEditModal(false)}
          onSave={handleCategorySave}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          title="Are you sure you want to delete this category?"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isLoading={isDeleting}
        />
      )}
    </>
  );
};

export default CategoryManagement;
